import { useEffect, useState } from "react";
import Layouts from "../../../components/Layouts";
import {
  obtenerDetallesRespuesta,
  obtenerFormularioCompleto,
  buscarRespuestasPorValor,
} from "../../../services/formularios";
import { useNavigate, useParams } from "react-router-dom";
import {
  message,
  Card,
  Typography,
  Space,
  Table,
  Tag,
  Image,
  Button,
  DatePicker,
  Input,
  Select,
  Modal,
} from "antd";
import dayjs from "dayjs";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as XLSX from 'xlsx';
import { DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Función para crear el ícono personalizado
const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-icon',
    html: `<div style="background-color: #1890ff; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

function VerRespuestas() {
  const { id } = useParams();
  const [respuestas, setRespuestas] = useState([]);
  const [detallesRespuestas, setDetallesRespuestas] = useState({});
  const [formulario, setFormulario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilterType, setSelectedFilterType] = useState("general");
  const [filters, setFilters] = useState({
    general: "",
    ...(formulario?.campos_formulario || []).reduce((acc, campo) => ({
      ...acc,
      [campo.label]: "",
    }), {}),
  });
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const renderValor = (valor, tipo) => {
    if (!valor) return "-";

    if (tipo === "imagen") {
      return (
        <Image
          width={200}
          src={`${process.env.REACT_APP_URL_DIRECTUS}/assets/${valor}`}
          alt="Imagen de respuesta"
        />
      );
    }

    if (tipo === "fecha") {
      return <Text>{dayjs(valor).format("YYYY-MM-DD")}</Text>;
    }

    // Para campos select y multiple, extraer solo el valor después de los dos puntos
    if (["select", "multiple_unico", "multiple_multiple"].includes(tipo)) {
      try {
        if (typeof valor === 'string' && valor.includes(':')) {
          const [_, value] = valor.split(':').map(s => s.trim());
          return <Text>{value}</Text>;
        }
      } catch (error) {
        console.error("Error al formatear valor:", error);
      }
    }

    return <Text>{valor}</Text>;
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Usar la función super optimizada que obtiene todo en una sola petición
      const formularioCompleto = await obtenerFormularioCompleto(id);
      
      setFormulario(formularioCompleto);
      setRespuestas(formularioCompleto.respuestas_formulario || []);

      // Organizar los detalles por respuesta
      const detallesObj = {};
      (formularioCompleto.respuestas_formulario || []).forEach(respuesta => {
        detallesObj[respuesta.id] = respuesta.respuestas_campos || [];
      });
      
      setDetallesRespuestas(detallesObj);
    } catch (error) {
      message.error("Error al cargar los datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [id]);
 
  const columns = [
    {
      title: "Fecha de creación",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => <Text>{dayjs(date).format("YYYY-MM-DD")}</Text>,
    },
    ...(formulario?.campos_formulario || []).map((campo) => ({
      title: campo.label,
      dataIndex: campo.id,
      key: campo.id,
      render: (_, record) => {
        const detalle = record.detalles?.find((d) => d.campo?.id === campo.id);
        return renderValor(detalle?.valor, campo.tipo);
      },
    })),
    {
      title: "Ubicación",
      key: "ubicacion",
      render: (_, record) => {
        if (record.latitude && record.longitude) {
          return (
            <Button
              type="primary"
              onClick={() => {
                setSelectedLocation({
                  lat: record.latitude,
                  lng: record.longitude,
                });
                setIsLocationModalVisible(true);
              }}
            >
              Ver ubicación
            </Button>
          );
        }
        return "-";
      },
    },
  ];

  const handleFilterTypeChange = (value) => {
    setSelectedFilterType(value);
    // Limpiar todos los filtros excepto el general
    setFilters({
      general: filters.general,
      ...(formulario?.campos_formulario || []).reduce((acc, campo) => ({
        ...acc,
        [campo.label]: "",
      }), {}),
    });
  };
  const tableData = respuestas.map((respuesta) => ({
    ...respuesta,
    detalles: detallesRespuestas[respuesta.id] || [],
    key: respuesta.id,
  }));

  const handleFilterChange = async (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));

    try {
      setLoading(true);
      let resultados;

      // Si el valor está vacío o es null, recargar todos los datos
      if (!value) {
        await cargarDatos();
        return;
      }

      if (type === "general") {
        resultados = await buscarRespuestasPorValor(id, value);
      } else {
        // Buscar por el campo específico
        const campoSeleccionado = formulario?.campos_formulario?.find(
          (campo) => campo.label === type
        );

        if (campoSeleccionado) {
          // Si es una fecha, formatearla
          const valorBusqueda = campoSeleccionado.tipo === "fecha" 
            ? dayjs(value).format("YYYY-MM-DD")
            : value;

          resultados = await buscarRespuestasPorValor(id, valorBusqueda);
        }
      }

      if (resultados && resultados.length > 0) {
        // Obtener los IDs de las respuestas filtradas
        const respuestaIds = [...new Set(resultados.map(detalle => detalle.respuestas_formulario.id))];
        
        // Cargar los detalles completos para cada respuesta filtrada
        const detallesPromises = respuestaIds.map(async (respuestaId) => {
          const detalles = await obtenerDetallesRespuesta(respuestaId);
          return { [respuestaId]: detalles };
        });

        const detallesArray = await Promise.all(detallesPromises);
        const detallesObj = detallesArray.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {}
        );

        // Crear el array de respuestas con sus detalles completos
        const respuestasFiltradas = respuestaIds.map(id => {
          const respuesta = resultados.find(r => r.respuestas_formulario.id === id)?.respuestas_formulario;
          return {
            ...respuesta,
            detalles: detallesObj[id] || [],
            key: id
          };
        });

        setRespuestas(respuestasFiltradas);
        setDetallesRespuestas(detallesObj);
      } else {
        // Si no hay resultados, limpiar los estados
        setRespuestas([]);
        setDetallesRespuestas({});
      }
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar los resultados");
    } finally {
      setLoading(false);
    }
  };

  const filterData = (data) => {
    return data.filter((item) => {
      // Si hay un tipo seleccionado y no coincide con el tipo del item, filtrarlo
      if (selectedFilterType !== "general") {
        const campoSeleccionado = formulario?.campos_formulario?.find(
          (campo) => campo.label === selectedFilterType
        );
        if (!campoSeleccionado) return true;

        const detalle = item.detalles?.find((d) => d.campo?.id === campoSeleccionado.id);
        const valor = detalle?.valor;

        if (campoSeleccionado.tipo === "fecha" && filters[campoSeleccionado.label]) {
          const itemDate = dayjs(valor);
          const filterDate = dayjs(filters[campoSeleccionado.label]);
          return itemDate.isSame(filterDate, "day");
        }

        return String(valor || "")
          .toLowerCase()
          .includes(String(filters[campoSeleccionado.label] || "").toLowerCase());
      }

      // Búsqueda general en todos los campos
      return item.detalles?.some((detalle) =>
        String(detalle.valor || "")
          .toLowerCase()
          .includes(String(filters.general || "").toLowerCase())
      );
    });
  };

  const filteredData = filterData(tableData);

  // Renderizar el filtro específico según el tipo seleccionado
  const renderFilterByType = () => {
    if (selectedFilterType === "general") {
      return (
        <Search
          placeholder="Buscar en todos los campos..."
          allowClear
          onChange={(e) => handleFilterChange("general", e.target.value)}
          style={{ width: "100%" }}
        />
      );
    }

    const campoSeleccionado = formulario?.campos_formulario?.find(
      (campo) => campo.label === selectedFilterType
    );

    if (!campoSeleccionado) return null;

    switch (campoSeleccionado.tipo) {
      case "fecha":
        return (
          <DatePicker
            placeholder={`Filtrar por ${campoSeleccionado.label}`}
            onChange={(date) => handleFilterChange(campoSeleccionado.label, date)}
            allowClear
            style={{ width: "100%" }}
          />
        );
      case "select":
      case "multiple_unico":
      case "multiple_multiple":
        let opciones = [];
        if (campoSeleccionado.opciones) {
          if (typeof campoSeleccionado.opciones === 'string') {
            // Si es string, intentar parsearlo
            try {
              const cleanString = campoSeleccionado.opciones.replace(/[{}"]/g, "");
              const pairs = cleanString.split(",").map((pair) => pair.trim());
              opciones = pairs
                .filter((pair) => pair.includes(":"))
                .map((pair) => {
                  const [_, value] = pair.split(":").map((s) => s.trim());
                  return value;
                });
            } catch (error) {
              console.error("Error al parsear opciones:", error);
            }
          } else if (typeof campoSeleccionado.opciones === 'object') {
            // Si es objeto, extraer los valores
            opciones = Object.values(campoSeleccionado.opciones);
          }
        }
        return (
          <Select
            placeholder={`Filtrar por ${campoSeleccionado.label}`}
            onChange={(value) => handleFilterChange(campoSeleccionado.label, value)}
            allowClear
            style={{ width: "100%" }}
          >
            {opciones.map((opcion) => (
              <Option key={opcion} value={opcion}>
                {opcion}
              </Option>
            ))}
          </Select>
        );
      default:
        return (
          <Input
            placeholder={`Filtrar por ${campoSeleccionado.label}`}
            onChange={(e) => handleFilterChange(campoSeleccionado.label, e.target.value)}
            allowClear
            style={{ width: "100%" }}
          />
        );
    }
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for Excel
      const excelData = filteredData.map(item => {
        const row = {
          'Fecha de creación': dayjs(item.date_created).format("YYYY-MM-DD"),
        };

        // Add form fields
        formulario?.campos_formulario?.forEach(campo => {
          const detalle = item.detalles?.find(d => d.campo?.id === campo.id);
          let valor = detalle?.valor || '-';

          // Format value based on field type
          if (campo.tipo === "fecha") {
            valor = dayjs(valor).format("YYYY-MM-DD");
          } else if (["select", "multiple_unico", "multiple_multiple"].includes(campo.tipo)) {
            if (typeof valor === 'string' && valor.includes(':')) {
              const [_, value] = valor.split(':').map(s => s.trim());
              valor = value;
            }
          }

          row[campo.label] = valor;
        });

        // Add location if available
        if (item.latitude && item.longitude) {
          row['Ubicación'] = `${item.latitude}, ${item.longitude}`;
        }

        return row;
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 15 }, // Fecha de creación
        ...(formulario?.campos_formulario || []).map(() => ({ wch: 20 })), // Form fields
        { wch: 25 }, // Ubicación
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Respuestas');

      // Generate Excel file with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      XLSX.writeFile(wb, `respuestas_${timestamp}.xlsx`);
      
      message.success('Archivo Excel generado exitosamente');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      message.error('Error al generar el archivo Excel');
    }
  };

  return (
    <Layouts>
      <div style={{ padding: "24px" }}>
        <Card>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Title level={2}>{formulario?.nombre}</Title>
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportExcel}
              >
                Exportar a Excel
              </Button>
            </Space>
            <Text type="secondary">{formulario?.descripcion}</Text>

            <Title level={4}>Respuestas</Title>
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: "16px" }}
            >
              <Space
                direction="vertical"
                style={{ width: "100%", marginBottom: "16px" }}
              >
                <Space style={{ width: "100%" }}>
                  <Select
                    defaultValue="general"
                    style={{ width: 200 }}
                    onChange={handleFilterTypeChange}
                  >
                    <Option value="general">Todos los campos</Option>
                    {formulario?.campos_formulario
                      ?.filter((campo) => campo.tipo !== "imagen")
                      .map((campo) => (
                        <Option key={campo.id} value={campo.label}>
                          {campo.label}
                        </Option>
                      ))}
                  </Select>
                  {renderFilterByType()}
                </Space>
              </Space>
            </Space>
            {filteredData.length > 0 ? (
              <Table
                columns={columns}
                dataSource={filteredData}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} respuestas`,
                }}
              />
            ) : (
              <Text>No hay respuestas registradas</Text>
            )}
          </Space>
        </Card>

        <Modal
          title="Ubicación"
          open={isLocationModalVisible}
          onCancel={() => setIsLocationModalVisible(false)}
          footer={null}
          width={800}
          bodyStyle={{ padding: 0 }}
        >
          {selectedLocation && (
            <div style={{ 
              width: "100%", 
              height: "600px",
              position: "relative"
            }}>
              <MapContainer
                center={[selectedLocation.lat, selectedLocation.lng]}
                zoom={15}
                style={{ 
                  height: "100%", 
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                whenReady={() => {
                  // Forzar actualización del mapa cuando esté listo
                  setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                  }, 100);
                }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker
                  position={[selectedLocation.lat, selectedLocation.lng]}
                  icon={createCustomIcon()}
                >
                  <Popup>
                    <div>
                      <div><strong>Latitud:</strong> {selectedLocation.lat}</div>
                      <div><strong>Longitud:</strong> {selectedLocation.lng}</div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          )}
        </Modal>
      </div>
    </Layouts>
  );
}

export default VerRespuestas;
